import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router";
import MapInput from "../../../../src/components/forms/MapInput";
import SelectBox from "../../../../src/components/forms/SelectBox";
import useGeocode from "../../../hooks/useGeocode";
import { Formik, Field, Form, FieldArray, useFormikContext } from "formik";
import { NumericFormat } from "react-number-format";
import { RadioGroup } from "@headlessui/react";
import * as Sentry from "@sentry/react";
import { Combobox, Transition } from "@headlessui/react";
import { createProperty } from "@/_actions/propertiesActions";
import { RefreshIcon } from "@heroicons/react/solid";
import getAcquisitionToken from "../../../../src/utils/getAcquisitionToken";
import validateMinPrice, { MIN_PROPERTY_PRICE_MESSAGE } from "@/utils/validateMinPrice";
import { useStates, useMunicipalities, useNeighborhoods } from "@/_actions/regionActions";
import { usePostHog } from "posthog-js/react";
import * as Yup from "yup";
import PropTypes from "prop-types";

const operationTypesArray = [
  { name: "Venta", value: "1" },
  { name: "Renta", value: "2" },
  { name: "Traspaso", value: "3" },
];

const propertyTypes = [
  { name: "casa", value: "18" },
  { name: "departamento", value: "19" },
  { name: "terreno", value: "34" },
  { name: "oficina", value: "28" },
  { name: "local", value: "26" },
  { name: "bodega", value: "15" },
  { name: "nave industrial", value: "45" },
  { name: "consultorio", value: "39" },
  { name: "edificio", value: "20" },
  { name: "rancho", value: "33" },
];

function removeEmpty(obj) {
  return Object.entries(obj)
    .filter(([_, v]) => v != "")
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
}

const UpdateLocationConfig = React.memo(({ values, updateConfig }) => {
  const { street, exterior_number, neighborhood, city, state } = values;
  const neighborhoodName = neighborhood?.name || neighborhood;
  const cityName = city?.name || city;
  const stateName = state?.name || state;

  useEffect(() => {
    const hasStreet = street && street.trim() !== "";
    const hasNumber = exterior_number && exterior_number.trim() !== "";
    const hasNeighborhood = neighborhood && neighborhoodName;
    const hasCity = city && cityName;
    const hasState = state && stateName;

    if (hasStreet && hasNumber && hasNeighborhood && hasCity && hasState) {
      const streetAddress = `${street.trim()}, ${exterior_number.trim()}`;

      updateConfig({
        street: streetAddress,
        neighborhood: neighborhoodName,
        city: cityName,
        state: stateName,
      });
    }
  }, [
    street,
    exterior_number,
    neighborhood,
    city,
    state,
    neighborhoodName,
    cityName,
    stateName,
    updateConfig,
  ]);

  return null;
});
UpdateLocationConfig.displayName = "UpdateLocationConfig";
UpdateLocationConfig.propTypes = {
  values: PropTypes.object.isRequired,
  updateConfig: PropTypes.func.isRequired,
};

const ScrollToError = () => {
  const { errors, isSubmitting, isValidating, submitCount } = useFormikContext();
  const lastSubmitCount = useRef(0);

  useEffect(() => {
    if (
      submitCount > 0 &&
      submitCount > lastSubmitCount.current &&
      !isSubmitting &&
      !isValidating &&
      Object.keys(errors).length > 0
    ) {
      lastSubmitCount.current = submitCount;
      // Pequeño delay para dejar que Formik renderice los mensajes de error
      const timer = setTimeout(() => {
        const errorKeys = Object.keys(errors);
        for (const key of errorKeys) {
          const element =
            document.getElementsByName(key)[0] ||
            document.getElementById(key) ||
            document.querySelector(`[data-testid="${key}Input"]`) ||
            document.querySelector(`[data-testid="${key}"]`);

          if (element) {
            // Calculamos el offset para no quedar debajo de headers fijos o sticky
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });

            if (element.focus) {
              element.focus({ preventScroll: true });
            }
            break;
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [submitCount, isSubmitting, isValidating, errors]);

  return null;
};

const PropertyAndLocation = () => {
  const [locationConfig, setLocationConfig] = useState({
    street: "",
    neighborhood: "",
    city: "",
    state: "",
  });

  const { data: states } = useStates();
  const { error, coordinates } = useGeocode(locationConfig);
  const navigate = useNavigate();
  const posthog = usePostHog();

  const [selectedState, setSelectedState] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [municipalityQuery, setMunicipalityQuery] = useState("");
  const [neighborhoodQuery, setNeighborhoodQuery] = useState("");

  const { data: municipalities } = useMunicipalities({
    state_id: selectedState?.id || selectedState,
  });

  const { data: neighborhoods } = useNeighborhoods({
    municipality: selectedMunicipality?.id || selectedMunicipality,
  });

  const filteredMunicipalities =
    municipalityQuery === ""
      ? municipalities
      : municipalities?.filter((municipality) =>
          municipality.name.toLowerCase().includes(municipalityQuery.toLowerCase()),
        );

  const filteredNeighborhoods =
    neighborhoodQuery === ""
      ? neighborhoods
      : neighborhoods?.filter((neighborhood) =>
          neighborhood.name.toLowerCase().includes(neighborhoodQuery.toLowerCase()),
        );

  const setOperationTypes = (checked, value, operationTypes, arrayHelpers) => {
    if (checked) {
      arrayHelpers.push(value);
    } else {
      const i = operationTypes.indexOf(value);
      arrayHelpers.remove(i);
    }
  };

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const onSubmit = async (values, actions) => {
    if (typeof values.state === "object" && values.state?.name) {
      values.state = values.state.name;
    }

    if (typeof values.city === "object" && values.city?.name) {
      values.municipality = values.city.id;
    }

    if (typeof values.neighborhood === "object" && values.neighborhood?.name) {
      values.colony = values.neighborhood.id;
    }

    const { setSubmitting } = actions;
    try {
      setSubmitting(true);
      const cleanValues = removeEmpty(values);
      const property = await createProperty({
        ...cleanValues,
        description: ".",
        latitude: coordinates.lat,
        longitude: coordinates.lng,
      });
      const { id } = property.data;
      localStorage.setItem("CYTpropertyUuid", id);
      if (posthog) {
        posthog.capture("Signup - Particular", {
          step: 2,
          stepName: "location_price",
          propertyType: values.property_type,
          operationType: values.operation_type,
          state: typeof values.state === "string" ? values.state : values.state?.name,
        });
      }
      setSubmitting(false);
      navigate("/unirse/amenidades");
    } catch (err) {
      setSubmitting(false);
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    const token = getAcquisitionToken();
    if (!token) {
      navigate("/unirse/particular");
    }
  }, [navigate]);

  const SignupSchema = Yup.object().shape({
    street: Yup.string().required("Campo requerido"),
    exterior_number: Yup.string().required("Campo requerido"),
    interior_number: Yup.string(),
    neighborhood: Yup.mixed().required("Campo requerido"),
    city: Yup.mixed().required("Campo requerido"),
    property_type: Yup.string().required("Se debe elegir una opción"),
    state: Yup.mixed().required("Campo requerido"),
    price_sale: Yup.string().when("operation_type", (operation_type, schema) => {
      return operation_type.includes("1")
        ? Yup.string()
            .required("Es necesario ingresar un precio")
            .test("min-price", MIN_PROPERTY_PRICE_MESSAGE, (val) => validateMinPrice(val))
        : schema;
    }),
    price_rent: Yup.string().when("operation_type", (operation_type, schema) => {
      return operation_type.includes("2")
        ? Yup.string()
            .required("Es necesario ingresar un precio")
            .test("min-price", MIN_PROPERTY_PRICE_MESSAGE, (val) => validateMinPrice(val))
        : schema;
    }),
    price_transfer: Yup.string().when("operation_type", (operation_type, schema) => {
      return operation_type.includes("3")
        ? Yup.string()
            .required("Es necesario ingresar un precio")
            .test("min-price", MIN_PROPERTY_PRICE_MESSAGE, (val) => validateMinPrice(val))
        : schema;
    }),
    operation_type: Yup.array().min(1, "Se debe elegir al menos una opción"),
  });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={{
        street: "",
        exterior_number: "",
        interior_number: "",
        neighborhood: "",
        city: "",
        state: "",
        property_type: "",
        currency: "MXN",
        price_sale: undefined,
        price_rent: undefined,
        price_transfer: undefined,
        operation_type: [],
      }}
      validationSchema={SignupSchema}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form onSubmit={handleSubmit} className="relative w-full space-y-3 md:mb-10">
          <ScrollToError />
          <UpdateLocationConfig
            values={values}
            touched={touched}
            updateConfig={setLocationConfig}
          />
          <RadioGroup
            id="property_type"
            className="hidden md:block"
            value={values.property_type}
            onChange={(val) => setFieldValue("property_type", val)}
          >
            <RadioGroup.Label className="form-title my-10">
              Tipo de Inmueble y Ubicación
            </RadioGroup.Label>
            <div className="m-auto grid grid-cols-2 gap-4">
              {propertyTypes.map((property, i) => (
                <RadioGroup.Option
                  key={i}
                  value={property.value}
                  className="relative cursor-pointer rounded-lg border border-gray-300 bg-white px-6 py-4 shadow-sm hover:border-gray-400 focus:outline-none sm:flex sm:justify-between md:h-11"
                >
                  {({ checked }) => (
                    <>
                      <div className="flex flex-1 items-center">
                        <div className="flex-1 text-sm">
                          <RadioGroup.Label
                            as="p"
                            className="text-center text-base font-medium capitalize leading-6 text-gray-900"
                          >
                            {property.name}
                          </RadioGroup.Label>
                        </div>
                      </div>

                      <div
                        className={classNames(
                          checked ? "border-yellow-500" : "border-transparent",
                          "pointer-events-none absolute -inset-px rounded-lg border-2",
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>

          <div className="block md:hidden">
            <label htmlFor="property_type" className="mt-8 block text-xl font-medium">
              Tipo de Inmueble y Ubicación
            </label>
            <select
              name="property_type"
              value={values.property_type}
              onChange={handleChange}
              onBlur={handleBlur}
              className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base capitalize focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            >
              <option value="" disabled>
                Selecciona tipo de inmueble
              </option>
              {propertyTypes.map((property, i) => (
                <option key={i} value={property.value} className="capitalize">
                  {property.name}
                </option>
              ))}
            </select>
          </div>
          {errors.property_type && touched.property_type && (
            <div className="text-sm text-red-600">{errors.property_type}</div>
          )}

          <h3 className="mt-8 text-xl font-medium">Ubicación</h3>
          <p className="mb-8 text-sm font-normal">
            Asegúrate que sea correcta. Una vez pagada, tu publicación no se puede editar.
          </p>

          <div className="grid-cols-2 gap-4 md:grid">
            <div>
              <label htmlFor="state" className="mb-1 text-sm font-medium">
                Estado
              </label>
              <Field
                as="select"
                className="select"
                name="state"
                onChange={(e) => {
                  const stateId = e.target.value;
                  const state = states?.find((s) => s.id == stateId);
                  setFieldValue("state", stateId);
                  setSelectedState(state);
                  setFieldValue("city", "");
                  setFieldValue("neighborhood", "");
                  setSelectedMunicipality(null);
                  setMunicipalityQuery("");
                  setNeighborhoodQuery("");
                }}
              >
                <option value="">Selecciona un estado</option>
                {states?.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name}
                  </option>
                ))}
              </Field>
              {errors.state && touched.state && (
                <div className="text-sm text-red-600">{errors.state}</div>
              )}
            </div>

            <div>
              <label htmlFor="city" className="mb-1 text-sm font-medium">
                Ciudad / Municipio
              </label>
              <Field name="city">
                {({ field, form }) => (
                  <Combobox
                    as="div"
                    value={field.value}
                    onChange={(municipality) => {
                      form.setFieldValue("city", municipality);
                      setSelectedMunicipality(municipality);
                      form.setFieldValue("neighborhood", "");
                      setNeighborhoodQuery("");
                    }}
                    disabled={!selectedState}
                  >
                    <Combobox.Input
                      name="city"
                      data-testid="municipalityInput"
                      autoComplete="off"
                      className="select disabled:bg-gray-100 disabled:cursor-not-allowed"
                      onChange={(event) => setMunicipalityQuery(event.target.value)}
                      displayValue={(municipality) => municipality?.name || ""}
                      placeholder={
                        selectedState ? "Buscar municipio..." : "Primero selecciona un estado"
                      }
                    />
                    <Transition
                      enter="transition duration-100 ease-out"
                      enterFrom="transform scale-95 opacity-0"
                      enterTo="transform scale-100 opacity-100"
                      leave="transition duration-75 ease-out"
                      leaveFrom="transform scale-100 opacity-100"
                      leaveTo="transform scale-95 opacity-0"
                      className="relative"
                    >
                      <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-50 p-2 shadow-md">
                        {filteredMunicipalities?.length === 0 && municipalityQuery !== "" ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No se encontraron municipios.
                          </div>
                        ) : (
                          filteredMunicipalities?.map((municipality) => (
                            <Combobox.Option
                              key={municipality.id}
                              value={municipality}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-4 pr-4 ${
                                  active ? "bg-gray-200 text-gray-900" : "text-gray-900"
                                }`
                              }
                            >
                              {municipality.name}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>
                )}
              </Field>
              {errors.city && touched.city && (
                <div className="text-sm text-red-600">{errors.city}</div>
              )}
            </div>

            <div>
              <label htmlFor="neighborhood" className="mb-1 text-sm font-medium">
                Colonia
              </label>
              <Field name="neighborhood">
                {({ field, form }) => (
                  <Combobox
                    as="div"
                    value={field.value}
                    onChange={(neighborhood) => {
                      form.setFieldValue("neighborhood", neighborhood);

                      setTimeout(() => {
                        if (
                          values.street &&
                          values.exterior_number &&
                          neighborhood &&
                          values.city &&
                          values.state
                        ) {
                          const streetAddress = `${values.street.trim()}, ${values.exterior_number.trim()}`;
                          const neighborhoodName = neighborhood?.name || neighborhood;
                          const cityName = values.city?.name || values.city;
                          const stateName = values.state?.name || values.state;

                          setLocationConfig({
                            street: streetAddress,
                            neighborhood: neighborhoodName,
                            city: cityName,
                            state: stateName,
                          });
                        }
                      }, 100);
                    }}
                    disabled={!selectedMunicipality}
                  >
                    <Combobox.Input
                      name="neighborhood"
                      autoComplete="off"
                      className="input disabled:bg-gray-100 disabled:cursor-not-allowed"
                      onChange={(event) => setNeighborhoodQuery(event.target.value)}
                      displayValue={(neighborhood) => neighborhood?.name || ""}
                      placeholder={
                        selectedMunicipality
                          ? "Buscar colonia..."
                          : "Primero selecciona un municipio"
                      }
                    />
                    <Transition
                      enter="transition duration-100 ease-out"
                      enterFrom="transform scale-95 opacity-0"
                      enterTo="transform scale-100 opacity-100"
                      leave="transition duration-75 ease-out"
                      leaveFrom="transform scale-100 opacity-100"
                      leaveTo="transform scale-95 opacity-0"
                      className="relative"
                    >
                      <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-50 p-2 shadow-md">
                        {filteredNeighborhoods?.length === 0 && neighborhoodQuery !== "" ? (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No se encontraron colonias.
                          </div>
                        ) : (
                          filteredNeighborhoods?.map((neighborhood) => (
                            <Combobox.Option
                              key={neighborhood.id}
                              value={neighborhood}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-4 pr-4 ${
                                  active ? "bg-gray-200 text-gray-900" : "text-gray-900"
                                }`
                              }
                            >
                              {neighborhood.name}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </Combobox>
                )}
              </Field>
              {errors.neighborhood && touched.neighborhood && (
                <div className="text-sm text-red-600">{errors.neighborhood}</div>
              )}
            </div>

            <div>
              <label htmlFor="street" className="mb-1 text-sm font-medium">
                Calle
              </label>
              <Field
                placeholder="Calle"
                name="street"
                className="input"
                type="text"
                onBlur={(e) => {
                  handleBlur(e);
                  setTimeout(() => {
                    const currentValues = {
                      street: e.target.value,
                      exterior_number: values.exterior_number,
                      neighborhood: values.neighborhood,
                      city: values.city,
                      state: values.state,
                    };

                    if (
                      currentValues.street &&
                      currentValues.exterior_number &&
                      currentValues.neighborhood &&
                      currentValues.city &&
                      currentValues.state
                    ) {
                      const streetAddress = `${currentValues.street.trim()}, ${currentValues.exterior_number.trim()}`;
                      const neighborhoodName =
                        currentValues.neighborhood?.name || currentValues.neighborhood;
                      const cityName = currentValues.city?.name || currentValues.city;
                      const stateName = currentValues.state?.name || currentValues.state;

                      setLocationConfig({
                        street: streetAddress,
                        neighborhood: neighborhoodName,
                        city: cityName,
                        state: stateName,
                      });
                    }
                  }, 100);
                }}
              />
              {errors.street && touched.street && (
                <div className="text-sm text-red-600">{errors.street}</div>
              )}
            </div>
            <div>
              <label htmlFor="exterior_number" className="mb-1 text-sm font-medium">
                Número
              </label>
              <Field
                placeholder="Número"
                name="exterior_number"
                className="input"
                type="text"
                onBlur={(e) => {
                  handleBlur(e);
                  setTimeout(() => {
                    const currentValues = {
                      street: values.street,
                      exterior_number: e.target.value,
                      neighborhood: values.neighborhood,
                      city: values.city,
                      state: values.state,
                    };

                    if (
                      currentValues.street &&
                      currentValues.exterior_number &&
                      currentValues.neighborhood &&
                      currentValues.city &&
                      currentValues.state
                    ) {
                      const streetAddress = `${currentValues.street.trim()}, ${currentValues.exterior_number.trim()}`;
                      const neighborhoodName =
                        currentValues.neighborhood?.name || currentValues.neighborhood;
                      const cityName = currentValues.city?.name || currentValues.city;
                      const stateName = currentValues.state?.name || currentValues.state;

                      setLocationConfig({
                        street: streetAddress,
                        neighborhood: neighborhoodName,
                        city: cityName,
                        state: stateName,
                      });
                    }
                  }, 100);
                }}
              />
              {errors.exterior_number && touched.exterior_number && (
                <div className="text-sm text-red-600">{errors.exterior_number}</div>
              )}
            </div>

            <div>
              <label htmlFor="interior_number" className="mb-1 text-sm font-medium">
                Interior
              </label>
              <Field placeholder="Interior" name="interior_number" className="input" type="text" />
              {errors.interior_number && touched.interior_number && (
                <div className="text-sm text-red-600">{errors.interior_number}</div>
              )}
            </div>
          </div>

          <div className="h-80 w-full md:h-60">
            <MapInput coordinates={coordinates} />
          </div>

          <h3 className="my-8 text-xl font-medium">Tipo de operación y precio</h3>

          <FieldArray
            name="operation_type"
            render={(arrayHelpers) => (
              <div
                id="operation_type"
                className="w-full gap-2 space-y-3 md:grid md:grid-cols-3 md:space-y-0"
              >
                {operationTypesArray.map((operation, index) => (
                  <SelectBox
                    key={index}
                    option={operation}
                    callBack={(checked, value) =>
                      setOperationTypes(checked, value, values.operation_type, arrayHelpers)
                    }
                  />
                ))}
              </div>
            )}
          />
          {errors.operation_type && touched.operation_type && (
            <div className="text-sm text-red-600">{errors.operation_type}</div>
          )}

          <div className="space-y-3">
            {values.operation_type.includes("1") && (
              <div>
                <label htmlFor="price_sale" className="block text-sm font-medium text-gray-700">
                  Precio Venta
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 z-10 flex items-center">
                    <label htmlFor="currency" className="sr-only">
                      Currency
                    </label>
                    <Field
                      as="select"
                      name="currency"
                      className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option>USD</option>
                      <option>MXN</option>
                    </Field>
                  </div>
                  <NumericFormat
                    id="price_sale"
                    prefix={"$"}
                    placeholder="0.00"
                    isNumericString={true}
                    thousandSeparator={true}
                    allowNegative={false}
                    value={values.price_sale}
                    onValueChange={(val) => setFieldValue("price_sale", val.floatValue)}
                    className="relative block w-full rounded-md border-gray-300 pl-16 pr-12  focus:border-indigo-500 focus:ring-indigo-500  sm:text-sm md:text-lg"
                  />
                </div>
                {errors.price_sale && touched.price_sale && (
                  <div className="text-sm text-red-600">{errors.price_sale}</div>
                )}
              </div>
            )}
            {values.operation_type.includes("2") && (
              <div>
                <label htmlFor="price_rent" className="block text-sm font-medium text-gray-700">
                  Precio Renta
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 z-10 flex items-center">
                    <label htmlFor="currency" className="sr-only">
                      Currency
                    </label>
                    <Field
                      as="select"
                      name="currency"
                      className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option>USD</option>
                      <option>MXN</option>
                    </Field>
                  </div>
                  <NumericFormat
                    id="price_rent"
                    prefix={"$"}
                    placeholder="0.00"
                    isNumericString={true}
                    thousandSeparator={true}
                    allowNegative={false}
                    value={values.price_rent}
                    onValueChange={(val) => setFieldValue("price_rent", val.floatValue)}
                    className="relative block w-full rounded-md border-gray-300 pl-16 pr-12  focus:border-indigo-500 focus:ring-indigo-500  sm:text-sm md:text-lg"
                  />
                </div>
                {errors.price_rent && touched.price_rent && (
                  <div className="text-sm text-red-600">{errors.price_rent}</div>
                )}
              </div>
            )}
            {values.operation_type.includes("3") && (
              <div>
                <label htmlFor="price_transfer" className="block text-sm font-medium text-gray-700">
                  Precio Traspaso
                </label>
                <div className="relative mt-1 rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 z-10 flex items-center">
                    <label htmlFor="currency" className="sr-only">
                      Currency
                    </label>
                    <Field
                      as="select"
                      name="currency"
                      className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-7 text-gray-500 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option>USD</option>
                      <option>MXN</option>
                    </Field>
                  </div>
                  <NumericFormat
                    id="price_transfer"
                    prefix={"$"}
                    placeholder="0.00"
                    isNumericString={true}
                    thousandSeparator={true}
                    allowNegative={false}
                    value={values.price_transfer}
                    onValueChange={(val) => setFieldValue("price_transfer", val.floatValue)}
                    className="relative block w-full rounded-md border-gray-300 pl-16 pr-12  focus:border-indigo-500 focus:ring-indigo-500  sm:text-sm md:text-lg"
                  />
                </div>
                {errors.price_transfer && touched.price_transfer && (
                  <div className="text-sm text-red-600">{errors.price_transfer}</div>
                )}
              </div>
            )}
          </div>
          <div className="sticky bottom-0 z-50 w-full bg-white p-3 md:relative md:bg-transparent md:p-5">
            <button
              disabled={isSubmitting}
              type="submit"
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-black py-2 px-4 text-lg font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <RefreshIcon className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                  <span className="ml-2">Procesando...</span>
                </div>
              ) : (
                <>
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg
                      className="h-5 w-5 text-black group-hover:text-black"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  Continuar
                </>
              )}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PropertyAndLocation;
